// src/utils/levels.ts
/**
 * The player experience curve, browser side.
 *
 * Must agree exactly with `functions/src/levels.ts`; `npm test` fails if it does not. The
 * curve was written out four separate times before — once here and three times in the
 * backend — which is the same kind of duplication that silently broke inventory ids.
 *
 * The growth rate tapers with level on purpose: a constant 9% made the first thirty levels
 * take about two and a half hours and level 150 take two thousand seven hundred hours for a
 * single level. The shape comes from `tools/sim/tune.mjs`, which searched for the settings
 * that produce the intended pace rather than having one picked by hand.
 */

/**
 * Growth applied when moving from `level` to the next.
 *
 * Three regimes, because one rate cannot serve a game that is meant to be brisk for a
 * month, paced for a year, and open-ended after that:
 *
 *   1..150    tapering from 13% to 2.5%, tuned so a regular player reaches 60 in the first
 *             month and 150 within a year. Untouched since it was measured and migrated.
 *   150..220  the rate decays to zero, so each level stops costing more than the last.
 *   220+      flat cost per level. Experience per hour still grows with the level bonus, so
 *             levels keep arriving — just slowly. Roughly four years to 200 and ten to 300
 *             at a heavy pace.
 *
 * There is deliberately NO level cap. The table below is generous rather than limiting; a
 * player who exhausts it has gone somewhere no design has been done for.
 */
const GROWTH_EARLY = 1.13;
const GROWTH_MID = 1.025;
const TAPER = 80;

/** Where the per-level cost stops compounding, and where it becomes flat. */
const FLATTEN_FROM = 150;
const FLATTEN_TO = 220;

/** How far the precomputed table reaches. Not a cap: see the note above. */
export const TABLE_LIMIT = 1000;

export const growthAt = (level: number): number => {
  const tapered = GROWTH_MID + (GROWTH_EARLY - GROWTH_MID) * Math.exp(-level / TAPER);
  if (level <= FLATTEN_FROM) return tapered;
  if (level >= FLATTEN_TO) return 1;
  const t = (level - FLATTEN_FROM) / (FLATTEN_TO - FLATTEN_FROM);
  return GROWTH_MID - (GROWTH_MID - 1) * t;
};

/** Cumulative experience per level, computed once rather than per lookup. */
const TOTALS: number[] = (() => {
    const totals = [0, 0];
    let current = 100;
    for (let level = 2; level <= TABLE_LIMIT + 1; level++) {
        totals[level] = totals[level - 1] + Math.floor(current);
        current *= growthAt(level);
    }
    return totals;
})();

export const calculateExpForLevel = (level: number): number => {
    if (level <= 1) return 0;
    return TOTALS[Math.min(level, TABLE_LIMIT + 1)];
};

export const calculateLevelFromExp = (totalExp: number): number => {
    let level = 1;
    while (level < TABLE_LIMIT && TOTALS[level + 1] <= totalExp) level++;
    return level;
};
